import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useCalculations, Calculation, MatrixConfig } from "@/hooks/useCalculations";
import { useFormQuestionsData } from "@/hooks/useFormRelationships";
import { useProject } from "@/hooks/useProject";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info, Users, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { HierarchicalGroupSelector } from "./HierarchicalGroupSelector";
import { MatrixRowSelector } from "@/components/MatrixRowSelector";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FormulaBuilder } from "./FormulaBuilder";

interface CreateCalculationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateId: number;
  jotformFormId?: string | null;
  jotformFormIdReferral?: string | null;
  calculation?: any | null;
}

export function CreateCalculationDialog({
  open,
  onOpenChange,
  templateId,
  jotformFormId,
  jotformFormIdReferral,
  calculation,
}: CreateCalculationDialogProps) {
  const { createCalculation, updateCalculation, isCreating, isUpdating } = useCalculations(templateId);
  const { formsData, isLoading, relationship } = useFormQuestionsData(jotformFormId);
  const { projectClient } = useProject();

  // Debug: ver qué trae el relationship y formsData
  useEffect(() => {
    console.log('🔍 DEBUG - jotformFormId:', jotformFormId);
    console.log('🔍 DEBUG - relationship:', relationship);
    console.log('🔍 DEBUG - formsData:', formsData);
    console.log('🔍 DEBUG - isLoading:', isLoading);
  }, [jotformFormId, relationship, formsData, isLoading]);

  const [name, setName] = useState("");
  const [calculationType, setCalculationType] = useState<'aggregate' | 'formula' | 'ranking'>('aggregate');
  const [operation, setOperation] = useState<'sum' | 'average' | 'count' | 'max' | 'min' | 'custom' | 'top' | 'bottom' | 'count_referrals'>('sum');
  const [calculationScope, setCalculationScope] = useState<'main' | 'referral' | 'comparison'>('main');
  const [groupBy, setGroupBy] = useState<'jotform_groups' | 'individual' | 'code' | 'launch'>('jotform_groups');
  const [selectedFormId, setSelectedFormId] = useState<string>('');
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedQuestion, setSelectedQuestion] = useState("");
  const [matrixConfig, setMatrixConfig] = useState<MatrixConfig>({});
  const [formula, setFormula] = useState("");
  const [referralId, setReferralId] = useState("");

  // Ranking-specific states
  const [rankingLimit, setRankingLimit] = useState(5);
  const [rankingFormat, setRankingFormat] = useState<'bullet' | 'numbered'>('bullet');
  const [rankingShowValues, setRankingShowValues] = useState(true);

  // Aggregation mode for matrix questions (flat = Excel style, default)
  const [aggregationMode, setAggregationMode] = useState<'nested' | 'flat'>('flat');

  // Obtener la data del formulario seleccionado
  const selectedFormData = formsData.find(form => form.formId === selectedFormId);
  const groups = selectedFormData?.groups || {};
  const questions = selectedFormData?.questions || [];
  // Reset form when dialog opens/closes or calculation changes
  useEffect(() => {
    if (open && calculation) {
      setName(calculation.name || "");
      setCalculationType(calculation.calculation_type || 'aggregate');
      setOperation(calculation.config?.operation || 'sum');
      setCalculationScope(calculation.config?.scope || 'main');
      setGroupBy(calculation.config?.groupBy || 'jotform_groups');
      setSelectedFormId(calculation.config?.formId || (relationship?.mainForm?.id || jotformFormId));
      setSelectedGroup(calculation.config?.group_name || "");
      // Soporte para ambas versiones: nueva (question_name) y antigua (question_names)
      setSelectedQuestion(calculation.config?.question_name || calculation.config?.question_names?.[0] || "");
      setMatrixConfig(calculation.config?.matrix_config || {});
      setFormula(calculation.config?.formula || "");
      setReferralId(calculation.config?.referral_id || "");

      // Ranking fields
      setRankingLimit(calculation.config?.limit || 5);
      setRankingFormat(calculation.config?.format || 'bullet');
      setRankingShowValues(calculation.config?.show_values !== false);

      // Aggregation mode
      setAggregationMode(calculation.config?.aggregation_mode || 'nested');
    } else if (open) {
      resetForm();
    }
  }, [open, calculation]);

  const resetForm = () => {
    setName("");
    setCalculationType('aggregate');
    setOperation('sum');
    setCalculationScope('main');
    setGroupBy('jotform_groups');
    setSelectedFormId(relationship?.mainForm?.id || jotformFormId);
    setSelectedGroup("");
    setSelectedQuestion("");
    setMatrixConfig({});
    setFormula("");
    setReferralId("");
    setRankingLimit(5);
    setRankingFormat('bullet');
    setRankingShowValues(true);
    setAggregationMode('flat');
  };

  // Initialize form selection when relationship loads
  useEffect(() => {
    if (relationship && !selectedFormId) {
      setSelectedFormId(relationship.mainForm?.id || jotformFormId || '');
    }
  }, [relationship, selectedFormId, jotformFormId]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      alert("Por favor, ingresa un nombre para el cálculo");
      return;
    }

    const calculationData: Omit<Calculation, 'id' | 'created_at' | 'updated_at'> = {
      template_id: templateId,
      name: name.trim(),
      calculation_type: calculationType,
      config: {
        operation,
        scope: calculationScope,
        formId: selectedFormId,

        // GroupBy for aggregate calculations
        ...(calculationType === 'aggregate' && { groupBy }),

        // Aggregation mode for average operations (flat = all values equal weight, nested = average of averages)
        ...(calculationType === 'aggregate' && operation === 'average' && { aggregation_mode: aggregationMode }),

        ...(selectedGroup && { group_name: selectedGroup }),
        ...(selectedQuestion && { question_name: selectedQuestion }),
        ...(Object.keys(matrixConfig).length > 0 && { matrix_config: matrixConfig }),
        ...(formula && { formula }),
        ...(referralId && { referral_id: referralId }),

        // Ranking-specific fields
        ...(calculationType === 'ranking' && {
          limit: rankingLimit,
          format: rankingFormat,
          show_values: rankingShowValues,
        }),
      },
    };

    try {
      if (calculation) {
        await updateCalculation({ id: calculation.id, ...calculationData });
      } else {
        await createCalculation(calculationData);
      }
      onOpenChange(false);
    } catch (error) {
      console.error("Error saving calculation:", error);
    }
  };

  // Función para obtener preguntas desde un path jerárquico
  const getQuestionsFromPath = (groups: any, path: string[]): string[] => {
    if (path.length === 0) return [];
    
    let current = groups[path[0]];
    if (!current) return [];
    
    // Si es el último nivel del path, devolver solo las preguntas de este grupo
    if (path.length === 1) {
      return current.questions || [];
    }
    
    // Si hay subgrupos, continuar navegando
    if (current.subgroups) {
      return getQuestionsFromPath(current.subgroups, path.slice(1));
    }
    
    return [];
  };

  // Obtener las preguntas del grupo seleccionado (maneja path jerárquico)
  const selectedGroupPath = selectedGroup ? selectedGroup.split(' > ') : [];
  const groupQuestions: string[] = selectedGroupPath.length > 0 
    ? getQuestionsFromPath(groups, selectedGroupPath)
    : [];

  // Obtener la pregunta seleccionada completa (acepta name o key)
  const selectedQuestionData = selectedQuestion
    ? questions?.find((q: any) => q.name === selectedQuestion || q.key === selectedQuestion)
    : null;

  // Determinar si la pregunta seleccionada es de tipo matriz
  const isMatrixQuestion = selectedQuestionData?.type === 'control_matrix';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-0">
        <div className="px-6 pt-6 pb-4">
          <DialogHeader>
            <DialogTitle>
              {calculation ? 'Editar Cálculo' : 'Nuevo Cálculo'}
            </DialogTitle>
            <DialogDescription>
              Crea cálculos basados en grupos de respuestas para usar en tus plantillas
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="flex-1 overflow-y-auto px-6 space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="calc-name">Nombre del Cálculo</Label>
              <Input
                id="calc-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ej: Promedio de Escucha"
              />
            </div>

            <div>
              <Label htmlFor="calc-type">Tipo de Cálculo</Label>
              <Select value={calculationType} onValueChange={(v: any) => setCalculationType(v)}>
                <SelectTrigger id="calc-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="aggregate">Agregación (suma, promedio, etc.)</SelectItem>
                  <SelectItem value="ranking">Ranking (top/bottom preguntas)</SelectItem>
                  <SelectItem value="formula">Fórmula personalizada</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Calculation Type Specific Options */}
          <Tabs value={calculationType} className="w-full">
            <TabsContent value="aggregate" className="space-y-4">
              <div>
                <Label htmlFor="operation">Operación</Label>
                <Select value={operation} onValueChange={(v: any) => setOperation(v)}>
                  <SelectTrigger id="operation">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sum">Suma</SelectItem>
                    <SelectItem value="average">Promedio</SelectItem>
                    <SelectItem value="count">Conteo</SelectItem>
                    <SelectItem value="max">Máximo</SelectItem>
                    <SelectItem value="min">Mínimo</SelectItem>
                    <SelectItem value="count_referrals">Contar Referidos</SelectItem>
                  </SelectContent>
                </Select>
                {operation === 'count_referrals' && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Cuenta el número total de referidos del formulario principal
                  </p>
                )}
              </div>

              {/* Aggregation mode selector - only show for average operation */}
              {operation === 'average' && (
                <div>
                  <Label htmlFor="aggregation-mode">Modo de Agregación</Label>
                  <Select value={aggregationMode} onValueChange={(v: 'nested' | 'flat') => setAggregationMode(v)}>
                    <SelectTrigger id="aggregation-mode">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nested">
                        <div className="flex flex-col items-start">
                          <span>Promedio de promedios (por pregunta)</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="flat">
                        <div className="flex flex-col items-start">
                          <span>Promedio plano (todos los valores)</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground mt-2">
                    {aggregationMode === 'nested'
                      ? 'Cada pregunta tiene el mismo peso, independiente del número de respuestas'
                      : 'Cada respuesta individual tiene el mismo peso (suma todos los valores / total de valores)'}
                  </p>
                </div>
              )}

              {/* Hide all other fields when operation is count_referrals */}
              {operation !== 'count_referrals' && (
                <>
                  <div>
                    <Label htmlFor="scope">Alcance del Cálculo</Label>
                    <Select value={calculationScope} onValueChange={(v: any) => setCalculationScope(v)}>
                      <SelectTrigger id="scope">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="main">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4" />
                            <span>Formulario Principal</span>
                          </div>
                        </SelectItem>
                        {relationship?.referralForms && relationship.referralForms.length > 0 && (
                          <>
                            <SelectItem value="referral">
                              <div className="flex items-center gap-2">
                                <Users className="h-4 w-4" />
                                <span>Formularios de Referidos</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="comparison">
                              <div className="flex items-center gap-2">
                                <Info className="h-4 w-4" />
                                <span>Comparación (Principal vs Referidos)</span>
                              </div>
                            </SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Form Selection */}
                  <div>
                    <Label htmlFor="formId">Formulario</Label>
                    <Select value={selectedFormId} onValueChange={setSelectedFormId}>
                      <SelectTrigger id="formId">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {/* Formulario principal */}
                        {relationship?.mainForm && (
                          <SelectItem value={relationship.mainForm.id}>
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4" />
                              <span>{relationship.mainForm.title || `Formulario Principal (${relationship.mainForm.id})`}</span>
                              <Badge variant="outline">Principal</Badge>
                            </div>
                          </SelectItem>
                        )}

                        {/* Formularios de referidos */}
                        {relationship?.referralForms?.map((form) => (
                          <SelectItem key={form.id} value={form.id}>
                            <div className="flex items-center gap-2">
                              <Users className="h-4 w-4" />
                              <span>{form.title || `Formulario de Referidos (${form.id})`}</span>
                              <Badge variant="secondary">Referido</Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {calculationScope === 'comparison' && (
                    <Alert>
                      <Info className="h-4 w-4" />
                      <AlertDescription>
                        Para comparaciones, el sistema calculará valores tanto del formulario principal como de los referidos,
                        permitiendo análisis como "Mi promedio: 8.5 vs Promedio de mis referidos: 7.2".
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Group By Selection */}
                  <div>
                    <Label htmlFor="groupBy">Agrupar Por</Label>
                    <Select value={groupBy} onValueChange={(v: any) => setGroupBy(v)}>
                      <SelectTrigger id="groupBy">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="jotform_groups">
                          <div className="flex items-center gap-2">
                            <span>Grupos de Preguntas (del formulario)</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="individual">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4" />
                            <span>Individual (cada submission)</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="code">
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4" />
                            <span>Equipo (mismo código)</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="launch">
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4" />
                            <span>Organización (todo el lanzamiento)</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-muted-foreground mt-1">
                      {groupBy === 'jotform_groups' && 'Calcular por grupos de preguntas del formulario'}
                      {groupBy === 'individual' && 'Calcular para cada submission individual'}
                      {groupBy === 'code' && 'Calcular promedio de todas las personas con el mismo código'}
                      {groupBy === 'launch' && 'Calcular promedio de toda la organización (lanzamiento completo)'}
                    </p>
                  </div>

                  {/* Hierarchical Group and Question Selection - Only show when groupBy is jotform_groups */}
                  {groupBy === 'jotform_groups' && (
                    <Alert>
                      <Info className="h-4 w-4" />
                      <AlertDescription>
                        Selecciona el grupo de preguntas sobre el cual calcular el {operation}.
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Hierarchical Group and Question Selection */}
                  {groupBy === 'jotform_groups' && (
                    <>
                      <HierarchicalGroupSelector
                        selectedGroup={selectedGroup}
                        selectedQuestion={selectedQuestion}
                        onGroupChange={setSelectedGroup}
                        onQuestionChange={(questionId, questionName) => {
                          setSelectedQuestion(questionId);
                          // Limpiar la configuración de matriz al cambiar de pregunta
                          setMatrixConfig({});
                        }}
                        jotformFormId={selectedFormId}
                        showQuestionSelector={true}
                        questionSelectorType="radio"
                      />

                      {/* Matrix Row Selection (usando componente reutilizable) */}
                      {isMatrixQuestion && selectedQuestion && (
                        <MatrixRowSelector
                          questionName={selectedQuestionData?.name || selectedQuestion}
                          jotformFormId={selectedFormId}
                          selectedRowId={matrixConfig.row_id}
                          onRowChange={(rowId, _rowText) => {
                            setMatrixConfig({ ...matrixConfig, row_id: rowId });
                          }}
                        />
                      )}
                    </>
                  )}
                </>
              )}
            </TabsContent>

            <TabsContent value="ranking" className="space-y-4">
              <div>
                <Label htmlFor="ranking-operation">Tipo de Ranking</Label>
                <Select value={operation} onValueChange={(v: any) => setOperation(v)}>
                  <SelectTrigger id="ranking-operation">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="top">Top (Mayores valores)</SelectItem>
                    <SelectItem value="bottom">Bottom (Menores valores)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="ranking-limit">Límite (Top N)</Label>
                <Input
                  id="ranking-limit"
                  type="number"
                  min={1}
                  max={20}
                  value={rankingLimit}
                  onChange={(e) => setRankingLimit(parseInt(e.target.value) || 5)}
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Cantidad de preguntas a mostrar (ej: Top 5)
                </p>
              </div>

              <div>
                <Label htmlFor="ranking-scope">Alcance del Ranking</Label>
                <Select value={calculationScope} onValueChange={(v: any) => setCalculationScope(v)}>
                  <SelectTrigger id="ranking-scope">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="main">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        <span>Individual (mis respuestas)</span>
                      </div>
                    </SelectItem>
                    {relationship?.referralForms && relationship.referralForms.length > 0 && (
                      <SelectItem value="referral">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          <span>Mis Referidos</span>
                        </div>
                      </SelectItem>
                    )}
                    <SelectItem value="code">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        <span>Mi Equipo (mismo código)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="launch">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        <span>Mi Organización (todo el lanzamiento)</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground mt-1">
                  {calculationScope === 'main' && 'Ranking basado solo en mis respuestas'}
                  {calculationScope === 'referral' && 'Ranking promedio de mis referidos'}
                  {calculationScope === 'code' && 'Ranking promedio de todas las personas con mi código'}
                  {calculationScope === 'launch' && 'Ranking promedio de toda la organización'}
                </p>
              </div>

              {/* Form Selection */}
              <div>
                <Label htmlFor="ranking-formId">Formulario</Label>
                <Select value={selectedFormId} onValueChange={setSelectedFormId}>
                  <SelectTrigger id="ranking-formId">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {relationship?.mainForm && (
                      <SelectItem value={relationship.mainForm.id}>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4" />
                          <span>{relationship.mainForm.title || `Formulario Principal (${relationship.mainForm.id})`}</span>
                          <Badge variant="outline">Principal</Badge>
                        </div>
                      </SelectItem>
                    )}
                    {relationship?.referralForms?.map((form) => (
                      <SelectItem key={form.id} value={form.id}>
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          <span>{form.title || `Formulario de Referidos (${form.id})`}</span>
                          <Badge variant="secondary">Referido</Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  <strong>Opcional:</strong> Puedes seleccionar un grupo específico para limitar el ranking solo a esas preguntas. Si no seleccionas ninguno, se rankeará todas las preguntas numéricas del formulario.
                </AlertDescription>
              </Alert>

              {/* Hierarchical Group Selector (opcional) */}
              <HierarchicalGroupSelector
                selectedGroup={selectedGroup}
                selectedQuestion=""
                onGroupChange={setSelectedGroup}
                onQuestionChange={() => {}}
                jotformFormId={selectedFormId}
                showQuestionSelector={false}
                questionSelectorType="radio"
              />

              <div>
                <Label htmlFor="ranking-format">Formato de Salida</Label>
                <Select value={rankingFormat} onValueChange={(v: any) => setRankingFormat(v)}>
                  <SelectTrigger id="ranking-format">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bullet">• Bulletpoints</SelectItem>
                    <SelectItem value="numbered">1. Numerado</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="show-values"
                  checked={rankingShowValues}
                  onChange={(e) => setRankingShowValues(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="show-values" className="cursor-pointer">
                  Mostrar valores numéricos
                </Label>
              </div>
            </TabsContent>

            <TabsContent value="formula" className="space-y-4">
              <FormulaBuilder
                templateId={templateId}
                formula={formula}
                onFormulaChange={setFormula}
              />
            </TabsContent>
          </Tabs>
        </div>

        <div className="px-6 py-4 border-t mt-auto">
          <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isCreating || isUpdating}
          >
            {calculation ? 'Actualizar' : 'Crear'} Cálculo
          </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
